import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useNotificationContext } from '@/modules/notifications/NotificationContext/hooks/useNotificationContext';
import { diagnostics } from '@/utils/diagnostics';

import { useEnqueueMemoWordExportMutation } from '../graphql/EnqueueMemoExport.generated';

interface UseEnqueueMemoWordExportOptions {
  householdId: string;
  source: string;
}

export interface UseEnqueueMemoWordExportResult {
  onExport: (memoId: string) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

export function useEnqueueMemoWordExport({
  householdId,
  source,
}: UseEnqueueMemoWordExportOptions): UseEnqueueMemoWordExportResult {
  const { showFeedback } = useFeedback();
  const { addAwaitingTargetIdForDownload } = useNotificationContext();
  const trackUserEvent = useTrackUserEvent();
  const progressFeedbackMessage =
    'Export in progress, this may take up to 1 minute';

  const [enqueueMemoWordExport, { loading, error }] =
    useEnqueueMemoWordExportMutation({
      onError: () => {
        showFeedback(
          `We weren't able to generate the Word document. Please try again.`
        );
      },
    });

  useReportError('Caught error when enqueuing memo Word export', error);

  const onExport = useCallback(
    async (memoId: string) => {
      await enqueueMemoWordExport({
        variables: {
          householdId,
          memoId,
        },
        onCompleted: () => {
          diagnostics.debug(
            '[MemoWordExport] Adding memo to awaiting downloads',
            {
              householdId,
              memoId,
            }
          );
          addAwaitingTargetIdForDownload(memoId);
          showFeedback(progressFeedbackMessage, {
            variant: 'success',
          });
          trackUserEvent('document memo exported', {
            householdId,
            memoId,
            source,
            format: 'word',
          });
        },
      });
    },
    [
      addAwaitingTargetIdForDownload,
      enqueueMemoWordExport,
      householdId,
      progressFeedbackMessage,
      showFeedback,
      source,
      trackUserEvent,
    ]
  );

  return {
    onExport,
    loading,
    error,
  };
}
